import { useState, useCallback } from 'react'

import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'

import { InviteUserRequestDTO } from '@/core/api/dtos/request/auth/invite'
import { UpdateUserRequestDTO } from '@/core/api/dtos/request/users/updateUser'
import { UserLoginToken } from '@/core/api/entities/user/userLoginToken'
import { authService } from '@/core/api/services/authService'
import { useAccountConfig } from '@/core/hooks/useAccountConfig'
import { useLoginAsUser } from '@/core/hooks/useUser'
import { useTranslation } from '@/core/i18n/useTranslation'
import { NotificationProps } from '@/shared/context/NotificationProvider'
import { extractErrorMessage } from '@/shared/utils/helpers'

import { useDisableUser } from './useDisableUser'
import { useUpdateUser } from './useUpdateUser'
import { User } from '../components/Users.types'

interface UseUserModalsReturn {
	selectedUser: User | null
	isEditModalVisible: boolean
	isRemoveModalVisible: boolean
	isLoginModalVisible: boolean
	isInviteModalVisible: boolean
	isUpdating: boolean
	isDisabling: boolean
	isLoggingIn: boolean
	isInviting: boolean
	openEditModal: (user: User) => void
	openRemoveModal: (user: User) => void
	openLoginModal: (user: User) => void
	openInviteModal: () => void
	closeEditModal: () => void
	closeRemoveModal: () => void
	closeLoginModal: () => void
	closeInviteModal: () => void
	handleEditUser: (userData: UpdateUserRequestDTO) => void
	handleRemoveUser: () => void
	handleInviteUser: (userData: InviteUserRequestDTO) => void
	handleLoginUser: () => void
}

/**
 * Custom hook to manage user modals state and handlers
 */
export const useUserModals = (
	showNotification: (props: NotificationProps) => void
): UseUserModalsReturn => {
	const { t } = useTranslation('common')
	const navigate = useNavigate()
	const queryClient = useQueryClient()

	const [selectedUser, setSelectedUser] = useState<User | null>(null)
	const [isEditModalVisible, setIsEditModalVisible] = useState(false)
	const [isRemoveModalVisible, setIsRemoveModalVisible] = useState(false)
	const [isLoginModalVisible, setIsLoginModalVisible] = useState(false)
	const [isInviteModalVisible, setIsInviteModalVisible] = useState(false)

	const accountConfig = useAccountConfig({})

	const { updateUser, isUpdating } = useUpdateUser(accountConfig)
	const { disableUser, isDisabling } = useDisableUser()

	const { mutate: loginAsUser, isPending: isLoggingIn } = useLoginAsUser({
		options: {
			onSuccess: (data: UserLoginToken) => {
				const token = data.token

				if (!token) {
					showNotification({
						type: 'error',
						message: t('account.users.login_error') || 'Error logging in',
						description: 'Token is missing from the response.',
					})
					return
				}

				closeLoginModal()
				navigate(`/login/token/${token}`)
			},
			onError: (error: any) => {
				showNotification({
					type: 'error',
					message: t('account.users.login_error') || 'Error logging in',
					description:
						error?.response?.data?.message ||
						t('account.users.login_error_description') ||
						'An error occurred while logging in. Please try again.',
				})
				closeLoginModal()
			},
		},
	})

	const { mutate: inviteUser, isPending: isInviting } = useMutation({
		mutationFn: (data: InviteUserRequestDTO) =>
			authService.invite(data, accountConfig),
		onSuccess: () => {
			showNotification({
				type: 'success',
				message:
					t('account.users.invite_success') || 'User invited successfully',
				description:
					t('account.users.invite_success_description') ||
					'The user has been invited successfully.',
			})
			queryClient.invalidateQueries({ queryKey: ['users'] })
			closeInviteModal()
		},
		onError: (error: any) => {
			const errorMessage =
				extractErrorMessage(error) ||
				t('account.users.invite_error_description') ||
				'An error occurred while inviting the user. Please try again.'

			showNotification({
				type: 'error',
				message: t('account.users.invite_error') || 'Error inviting user',
				description: errorMessage,
			})
		},
	})

	const openEditModal = useCallback((user: User) => {
		setSelectedUser(user)
		setIsEditModalVisible(true)
	}, [])

	const openRemoveModal = useCallback((user: User) => {
		setSelectedUser(user)
		setIsRemoveModalVisible(true)
	}, [])

	const openLoginModal = useCallback((user: User) => {
		setSelectedUser(user)
		setIsLoginModalVisible(true)
	}, [])

	const openInviteModal = useCallback(() => {
		setIsInviteModalVisible(true)
	}, [])

	const closeEditModal = useCallback(() => {
		setIsEditModalVisible(false)
		setSelectedUser(null)
	}, [])

	const closeRemoveModal = useCallback(() => {
		setIsRemoveModalVisible(false)
		setSelectedUser(null)
	}, [])

	const closeLoginModal = useCallback(() => {
		setIsLoginModalVisible(false)
		setSelectedUser(null)
	}, [])

	const closeInviteModal = useCallback(() => {
		setIsInviteModalVisible(false)
	}, [])

	const handleEditUser = useCallback(
		async (userData: UpdateUserRequestDTO) => {
			if (!selectedUser?.id) {
				showNotification({
					type: 'error',
					message: t('account.users.edit_error') || 'Error updating user',
					description:
						t('account.users.edit_error_description') || 'User ID is missing.',
				})
				return
			}

			await updateUser(parseInt(selectedUser.id), userData, {
				onSuccess: () => {
					showNotification({
						type: 'success',
						message:
							t('account.users.edit_success') || 'User updated successfully',
						description:
							t('account.users.edit_success_description') ||
							'The user has been updated successfully.',
					})
					closeEditModal()
				},
				onError: (error: any) => {
					const errorMessage =
						extractErrorMessage(error) ||
						t('account.users.edit_error_description') ||
						'An error occurred while updating the user. Please try again.'

					showNotification({
						type: 'error',
						message: t('account.users.edit_error') || 'Error updating user',
						description: errorMessage,
					})
				},
			})
		},
		[selectedUser, updateUser, showNotification, t, closeEditModal]
	)

	const handleRemoveUser = useCallback(() => {
		if (!selectedUser?.id) {
			showNotification({
				type: 'error',
				message:
					t('account.user_profile.account.remove.error_title') ||
					'Error removing user',
				description:
					t('account.user_profile.account.remove.user_id_missing') ||
					'User ID is missing.',
			})
			closeRemoveModal()
			return
		}

		disableUser(parseInt(selectedUser.id), {
			onSuccess: () => {
				showNotification({
					type: 'success',
					message:
						t('account.user_profile.account.remove.success_title') ||
						'User removed successfully',
					description:
						t('account.user_profile.account.remove.success_description') ||
						'The user has been removed successfully.',
				})
				closeRemoveModal()
			},
			onError: (error: any) => {
				showNotification({
					type: 'error',
					message:
						t('account.user_profile.account.remove.error_title') ||
						'Error removing user',
					description:
						extractErrorMessage(error) ||
						t('account.user_profile.account.remove.error_description') ||
						'An error occurred while removing the user. Please try again.',
				})
				closeRemoveModal()
			},
		})
	}, [selectedUser, disableUser, showNotification, t, closeRemoveModal])

	const handleInviteUser = useCallback(
		(userData: InviteUserRequestDTO) => {
			inviteUser(userData)
		},
		[inviteUser]
	)

	const handleLoginUser = useCallback(() => {
		if (!selectedUser?.id) {
			showNotification({
				type: 'error',
				message: t('account.users.login_error') || 'Error logging in',
				description:
					t('account.user_profile.account.remove.user_id_missing') ||
					'User ID is missing.',
			})
			return
		}

		if (isLoggingIn) {
			return
		}

		loginAsUser(parseInt(selectedUser.id))
	}, [selectedUser, loginAsUser, isLoggingIn, showNotification, t])

	return {
		selectedUser,
		isEditModalVisible,
		isRemoveModalVisible,
		isLoginModalVisible,
		isInviteModalVisible,
		isUpdating,
		isDisabling,
		isLoggingIn,
		isInviting,
		openEditModal,
		openRemoveModal,
		openLoginModal,
		openInviteModal,
		closeEditModal,
		closeRemoveModal,
		closeLoginModal,
		closeInviteModal,
		handleEditUser,
		handleRemoveUser,
		handleInviteUser,
		handleLoginUser,
	}
}
